import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaCheck, FaEye, FaMoneyBillWave } from 'react-icons/fa';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageHeading from '../PageHeading';
import axios from 'axios';
import ExportOptions from '../ExportOptions';
import TableSkeleton from '../TableSkeleton';
import ConfirmActionModal from '../ConfirmDeleteModal';
import ViewBillModal from './ViewBillModal';
import GenerateBillModal from './GenerateBillModal';
import { BsThreeDotsVertical } from 'react-icons/bs';
import SmartDropdown from '../Shared/SmartDropdown';
import PharmacyBillAddPaymentModal from './PharmacyBillAddPaymentModal';
import Pagination from '../Shared/Pagination';

// Debounce hook to delay state updates
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Environment variables
const API_URL = process.env.REACT_APP_API_URL || 'https://api.muktihospital.com/api/medicine-bill';
const API_KEY = process.env.REACT_APP_API_KEY || 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079';
const DEBUG = process.env.NODE_ENV !== 'production';

const PharmacyBill = () => {
  // State variables
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [bills, setBills] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewBill, setViewBill] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoadingBills, setIsLoadingBills] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [editBillId, setEditBillId] = useState(null);
  
  // Use useRef to store the Cancel Token source to prevent race conditions
  const cancelTokenSource = useRef(null);

  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const actionButtonClass = 'size-7 rounded-full text-sm flex items-center justify-center transition-all duration-300';

  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  const axiosInstance = useMemo(() => axios.create({
    headers: { 'x-api-key': API_KEY },
    timeout: 10000,
  }), []);


  // Fetch bills with cancellation logic
  const fetchBills = useCallback(async (page, query) => {
    // Cancel the previous request if it exists
    if (cancelTokenSource.current) {
      cancelTokenSource.current.cancel('New request initiated, canceling the previous one.');
    }
    // Create a new cancel token source for the current request
    cancelTokenSource.current = axios.CancelToken.source();

    setIsLoadingBills(true);
    try {
      const url =  `${API_URL}/list/?page=${page}&limit=${itemsPerPage}${query ? `&search=${encodeURIComponent(query)}` : ''}`;

      const response = await axiosInstance.get(url, { cancelToken: cancelTokenSource.current.token });
      
      const billData = Array.isArray(response.data?.bills) ? [...response.data.bills] : [];
      console.log(billData)
      setBills(billData);
      setTotalPages(response.data?.totalPages || Math.ceil((response.data?.total || 0) / itemsPerPage) || 1);
      setTotalRecords(response.data?.total || 0);

      // If no data and on a page > 1, go to the previous page
      if (billData.length === 0 && page > 1) {
        setCurrentPage(page - 1);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        // Request was canceled, so no need to show an error
        if (DEBUG) console.log('Request canceled:', err.message);
      } else {
        // Handle other network or server errors
        console.error('DEBUG: fetchBills error', err);
        toast.error('Failed to load bills', { autoClose: 2000 });
        setBills([]);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } finally {
      setIsLoadingBills(false);
      cancelTokenSource.current = null; // Reset the token after the request is complete
    }
  }, [axiosInstance, itemsPerPage]);

  // Use a single useEffect hook for data fetching
  useEffect(() => {
    fetchBills(currentPage, debouncedSearchQuery);
    return () => {
      // Clean up by canceling the request when the component unmounts
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel('Component unmounted.');
      }
    };
  }, [currentPage, debouncedSearchQuery, fetchBills]);

  // Handle deletion
  const handleDelete = useCallback(async (billIds) => {
    setIsDeleting(true);
    const ids = Array.isArray(billIds) ? billIds : [billIds];
    if (!ids.length) {
      toast.error('No bills selected', { autoClose: 2000 });
      setIsDeleting(false);
      return;
    }
    try {
      const results = await Promise.allSettled(
        ids.map((id) => axiosInstance.delete(`${API_URL}/${id}`))
      );
      const errors = results
        .map((result, index) => result.status === 'rejected' && ({ id: ids[index], error: result.reason?.message }))
        .filter(Boolean);
      if (errors.length) {
        errors.forEach((err) => toast.error(`Failed to delete bill ${err.id}`, { autoClose: 2000 }));
      } else {
        toast.success(`${ids.length} bill${ids.length > 1 ? 's' : ''} deleted`, { autoClose: 2000 });
      }

      // Re-fetch data from the current page to ensure fresh data
      await fetchBills(currentPage, debouncedSearchQuery);

    } catch (err) {
      if (DEBUG) console.error('DEBUG: handleDelete error', err);
      toast.error('Failed to delete bill(s)', { autoClose: 2000 });
    } finally {
      setIsDeleting(false);
      setPendingDeleteId(null);
      setDeleteModalOpen(false);
      setSelectedRows([]);
    }
  }, [currentPage, debouncedSearchQuery, fetchBills, axiosInstance]);

  // Open deletion confirmation modal
  const openDeleteConfirmation = useCallback((billId) => {
    if (!billId) {
      toast.error('No bill selected', { autoClose: 2000 });
      return;
    }
    setPendingDeleteId(billId);
    setDeleteModalOpen(true);
  }, []);

  // View bill details
  const handleView = useCallback(async (bill) => {
    if (!bill?.id) {
      toast.error('Invalid bill ID', { autoClose: 2000 });
      return;
    }
    setIsViewing(true);
    try {
      const response = await axiosInstance.get(`${API_URL}/${bill.id}`);
      if (response.data?.bill) {
        setViewBill(response.data.bill);
        setViewModalOpen(true);
      } else {
        toast.error('No bill data found', { autoClose: 2000 });
      }
    } catch (err) {
      toast.error('Failed to load bill details', { autoClose: 2000 });
    } finally {
      setIsViewing(false);
    }
  }, [axiosInstance]);

  const handleEdit = (bill) => {
    setEditBillId(bill.id);
    setGenerateBillModalOpen(true);
  };

  const handleSingleDelete = useCallback((billId) => {
    openDeleteConfirmation(billId);
  }, [openDeleteConfirmation]);

  const handlePageChange = useCallback((page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
      setSelectedRows([]);
    }
  }, [totalPages]);

  const handleSelectAll = useCallback((e) => {
    const validIds = bills.filter((bill) => bill.id != null).map((bill) => bill.id);
    setSelectedRows(e.target.checked ? validIds : []);
  }, [bills]);

  const handleRowSelect = useCallback((billId) => {
    if (billId == null) {
      if (DEBUG) console.warn('DEBUG: handleRowSelect: Invalid billId', { billId });
      return;
    }
    setSelectedRows((prev) =>
      prev.includes(billId) ? prev.filter((id) => id !== billId) : [...prev, billId]
    );
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    // Reset to the first page when a search is initiated
    setCurrentPage(1);
  }, []);

  const headers = ['SL No', 'BILL NO', 'DATE', 'NET AMOUNT (৳)', 'DUE AMOUNT (৳)', 'SUBMITTED BY'];

  const dataMapper = useMemo(() => {
    let counter = 0;
    return (item) => {
      counter += 1;
      return [
        counter,
        item.billNumber || '-',
        item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-',
        item.netAmount?.toFixed(2) || '0.00',
        item.returnAmount?.toFixed(2) || '0.00',
        item.userName || '-',
      ].join("\t");
    };
  }, []);

  const breadcrumbs = useMemo(() => [
    { label: 'Dashboard', url: '/' },
    { label: 'Pharmacy'},
    { label: 'Pharmacy Bills' },
  ], []);

  const toggleDropdown = useCallback(() => setDropdownOpen((prev) => !prev), []);

  const skeletonColumns = useMemo(() => [
    { width: 20, height: 16, className: 'px-4 py-4' },
    { width: 60, height: 16, className: 'px-4 py-4' },
    { width: 80, height: 16, className: 'px-4 py-4' },
    { width: 100, height: 16, className: 'px-4 py-4' },
    { width: 100, height: 16, className: 'px-4 py-4' },
    { width: 100, height: 16, className: 'px-4 py-4' },
    { width: 100, height: 16, className: 'px-4 py-4' },
    { width: 80, height: 16, className: 'px-4 py-4' },
  ], []);

  const allVisibleSelected = useMemo(() => 
    bills.length > 0 && bills.every((bill) => bill.id && selectedRows.includes(bill.id)),
    [bills, selectedRows]
  );

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Pharmacy Bills" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">Bill List</h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => openDeleteConfirmation(selectedRows)}
                className={`${actionButtonClass} size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center`}
                disabled={isLoadingBills || isDeleting}
                aria-label="Delete selected bills"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by bill no or patient..."
              value={searchQuery}
              onChange={handleSearchChange}
              disabled={isLoadingBills}
              aria-label="Search bills by bill number or patient name"
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => setGenerateBillModalOpen(true)}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 disabled:opacity-50"
                disabled={isLoadingBills}
                aria-label="Generate new bill"
              >
                Generate Bill
              </button>
              <ExportOptions
                data={bills}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Pharmacy Bill List"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        <GenerateBillModal
          isOpen={generateBillModalOpen}
          onClose={() => setGenerateBillModalOpen(false)}
          onSave={async (message) => {
            if (DEBUG) console.log('DEBUG: GenerateBillModal onSave', { message });
            await fetchBills(currentPage, debouncedSearchQuery);
            toast.success(message || 'Bill added successfully', { autoClose: 2000 });
          }}
        />
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table" role="table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print" scope="col">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="selectAll"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={allVisibleSelected}
                        disabled={bills.length === 0 || isLoadingBills}
                        aria-label="Select all bills on this page"
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="selectAll"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  {headers.map((header, idx) => (
                    <th
                      key={idx}
                      className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3"
                      scope="col"
                    >
                      {header}
                    </th>
                  ))}
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print" scope="col">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoadingBills ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : bills.length === 0 ? (
                  <tr className="print-hidden">
                    <td colSpan={9} className="text-center py-5 text-gray-500" role="cell">
                      {searchQuery
                        ? "No bills found matching your search."
                        : "No bills available."}
                    </td>
                  </tr>
                ) : (
                  bills.map((bill, index) => (
                    <tr key={bill.id} className="border-b hover:bg-gray-50" role="row">
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print" role="cell">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id={`bill-${index}`}
                            className="hidden peer"
                            checked={selectedRows.includes(bill.id)}
                            onChange={() => handleRowSelect(bill.id)}
                            disabled={isLoadingBills || isDeleting}
                            aria-label={`Select bill ${bill.billNumber || 'unknown'}`}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`bill-${index}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {bill.billNumber || '-'}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {bill.createdAt
                          ? new Date(bill.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                          : '-'}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {bill.netAmount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {bill.dueAmount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3" role="cell">
                        {bill.userName || 'Unknown'}
                      </td>
                      <td className="p-3 text-right print-hidden-in-print" role="cell">
                            {/* Trigger Button */}
                            <button
                              ref={(el) => (dropdownTriggerRefs.current[bill.id] = el)}
                              onClick={() => setActiveDropdown(activeDropdown === bill.id ? null : bill.id)}
                              className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                              disabled={isLoadingBills || isDeleting}
                              aria-label="Actions"
                            >
                              <BsThreeDotsVertical size={14} />
                            </button>

                            {/* Smart Dropdown */}
                            <SmartDropdown
                              isOpen={activeDropdown === bill.id}
                              triggerRef={{ current: dropdownTriggerRefs.current[bill.id] }}
                              onClose={() => setActiveDropdown(null)}
                              options={{ width: 160, align: 'right' }}
                            >
                              <div className="py-1">
                                {/* 1. Add Money Button */}
                                <button
                                  onClick={() => { handleAddMoney(bill); setActiveDropdown(null); }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FaMoneyBillWave className="text-blue-500" /> Add Money
                                </button>

                                {/* 1. View Details Button */}
                                <button
                                  onClick={() => {
                                    handleView(bill);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FaEye className="text-blue-500" /> View Details
                                </button>

                                {/* 2. Delete Bill Button */}
                                <button
                                  onClick={() => {
                                    handleSingleDelete(bill.id);
                                    setActiveDropdown(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                                >
                                  <RiDeleteBin6Line className="text-red-500" /> Delete
                                </button>
                              </div>
                            </SmartDropdown>
                          </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (DEBUG) console.log('DEBUG: Closing ConfirmActionModal');
          setDeleteModalOpen(false);
          setPendingDeleteId(null);
        }}
        onConfirm={() => handleDelete(pendingDeleteId || selectedRows)}
        ids={pendingDeleteId ? [pendingDeleteId] : selectedRows}
        entityName="bill"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          pendingDeleteId
            ? 'Do you really want to delete this bill?'
            : selectedRows.length === 1
            ? 'Do you really want to delete this bill?'
            : `Do you really want to delete ${selectedRows.length} bills?`
        }
      />
      <ViewBillModal
        isOpen={viewModalOpen}
        onClose={() => {
          if (DEBUG) console.log('DEBUG: Closing ViewBillModal');
          setViewModalOpen(false);
          setViewBill(null);
          setDropdownOpen(false);
        }}
        bill={viewBill}
        onEdit={async (message) => {
          if (DEBUG) console.log('DEBUG: ViewBillModal onEdit', { message });
          setIsLoadingBills(true);
          try {
            await fetchBills(currentPage, debouncedSearchQuery);
            toast.success(message || 'Bill updated successfully', { autoClose: 2000 });
          } catch (err) {
            toast.error('Failed to refresh bills', { autoClose: 2000 });
          } finally {
            setViewModalOpen(false);
            setViewBill(null);
            setIsLoadingBills(false);
          }
        }}
        onDelete={(billId) => {
          if (DEBUG) console.log('DEBUG: ViewBillModal onDelete', { billId });
          handleDelete(billId);
          setViewModalOpen(false);
        }}
      />
      <PharmacyBillAddPaymentModal 
          isOpen={paymentModalOpen} 
          onClose={() => setPaymentModalOpen(false)} 
          bill={paymentBillData} 
          onPaymentSuccess={() => fetchBills(currentPage, debouncedSearchQuery)} 
      />
    </div>
  );
};

export default PharmacyBill;